import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import {
  MapPin, ShieldCheck, Headphones, PackageSearch,
  Home, CalendarPlus, Monitor, Users, Trees, Truck,
  Star, BadgeCheck, Clock3, Headset, ThumbsUp, Briefcase,
  ShoppingCart, ShoppingBag, UserCircle2, ChevronDown,
  Zap, CalendarDays, Crown, LayoutGrid, ArrowRight,
} from "lucide-react";
import config, { getUserCity, setUserCity } from "../../config";
import clickNidoLogo from "../images/stage_01/ClickNido_Logo.png";
import headerHomeServiceImg from "../images/stage_01/Header Home Service.png";
import headerTechnicalServiceImg from "../images/stage_01/Header Technical Service.png";
import headerCaretakerImg from "../images/stage_01/Header Caretaker.png";
import headerTransportImg from "../images/stage_01/Header Transport.png";
import headerSpecialServiceImg from "../images/stage_01/Header Special Service.png";
import headerOutdoorImg from "../images/stage_01/Header Outdoor.png";
import headerCorporateServiceImg from "../images/stage_01/Header Corporate Service.png";

// ─────────────────────────────────────────────────────────────────────────────
// SHARED AUTH HELPERS — call these from login page after successful login
// ─────────────────────────────────────────────────────────────────────────────

export const saveLoggedInUser = (userObj, token = "") => {
  try {
    localStorage.setItem("currentUser", JSON.stringify(userObj));
    if (token) localStorage.setItem("authToken", token);
    window.dispatchEvent(new Event("auth-updated"));
  } catch (e) { console.error("[Auth] save failed:", e); }
};

export const getSafeLoggedInUser = () => {
  try {
    const raw   = localStorage.getItem("currentUser");
    const token = localStorage.getItem("authToken") || "";
    if (!raw && !token) return null;
    const user = raw ? JSON.parse(raw) : {};
    return { ...user, token };
  } catch { return null; }
};

export const getAuthMobile = () => {
  const u = getSafeLoggedInUser();
  return u?.mobile || u?.phone || u?.mobile_number || "";
};

export const getAuthToken = () =>
  localStorage.getItem("authToken")    ||
  localStorage.getItem("accessToken")  ||
  localStorage.getItem("access_token") ||
  localStorage.getItem("token") || "";

export const isUserLoggedIn = () => {
  const u = getSafeLoggedInUser();
  return !!(u?.token || u?.id || u?.mobile || u?.phone);
};

// ─────────────────────────────────────────────────────────────────────────────
// SHARED CART HELPERS — exported so any service page can use them
// ─────────────────────────────────────────────────────────────────────────────

const BASE_URL = process.env.REACT_APP_API_BASE_URL || "http://localhost:8000";

const cartAuthHeaders = () => ({
  "Content-Type": "application/json",
  ...(getAuthToken() ? { Authorization: `Bearer ${getAuthToken()}` } : {}),
});

/**
 * The main function every service page calls on "Add to Cart".
 *
 * Flow:
 *  1. If NOT logged in  → redirect to login page (saves redirectTo + item in sessionStorage)
 *  2. If logged in      → POST item to MongoDB /add/ immediately
 *                       → update localStorage "cartItems" so Header badge updates
 *                       → dispatch "cart-updated" event
 *
 * @param {object}   service   – { id, title, name, price, image, subtitle, category }
 * @param {function} navigate  – from useNavigate()
 * @param {string}   redirectAfterLogin – page to return to after login (default current path)
 */
export const handleAddToCartGlobal = async (service, navigate, redirectAfterLogin = "") => {
  const loggedIn = isUserLoggedIn();

  if (!loggedIn) {
    // Save the item and where to return so login page can redirect back
    const returnPath = redirectAfterLogin || window.location.pathname;
    sessionStorage.setItem("pendingCartItem", JSON.stringify(service));
    sessionStorage.setItem("postLoginRedirect", returnPath);
    navigate("/user/user-login", {
      state: { redirectTo: returnPath, pendingCartItem: service },
    });
    return false; // did not add
  }

  const mobile = getAuthMobile();
  if (!mobile) {
    console.error("[Cart] No mobile found for logged-in user");
    return false;
  }

  // Optimistically update localStorage first (instant UI feedback)
  try {
    const existing = JSON.parse(localStorage.getItem("cartItems") || "[]");
    const idx = existing.findIndex((i) => String(i.id) === String(service.id));
    if (idx > -1) {
      existing[idx].qty = Number(existing[idx].qty || 1) + 1;
    } else {
      existing.push({
        id:       String(service.id),
        title:    service.title || service.name || "Service",
        name:     service.name  || service.title || "Service",
        subtitle: service.subtitle || service.short || "",
        short:    service.short || service.subtitle || "",
        price:    Number(service.price || 0),
        image:    service.image || null,
        category: service.category || "",
        qty:      1,
      });
    }
    localStorage.setItem("cartItems", JSON.stringify(existing));
    window.dispatchEvent(new Event("cart-updated")); // update Header badge instantly
  } catch (e) { console.error("[Cart] localStorage update failed:", e); }

  // Then POST to MongoDB
  try {
    const url = `${BASE_URL}/api/user/user-cart/${encodeURIComponent(mobile)}/add/`;
    console.log(`[Cart] POST ${url}`);
    const res = await fetch(url, {
      method: "POST",
      credentials: "include",
      headers: cartAuthHeaders(),
      body: JSON.stringify({
        items: [{
          id:       String(service.id),
          title:    service.title || service.name,
          name:     service.name  || service.title,
          subtitle: service.subtitle || service.short || "",
          price:    Number(service.price || 0),
          image:    service.image || null,
          category: service.category || "",
          qty:      1,
        }],
      }),
    });
    const text = await res.text();
    console.log(`[Cart] POST ← ${res.status}`, text);
    return res.ok;
  } catch (err) {
    console.error("[Cart] POST failed:", err.message);
    return false;
  }
};

/**
 * Remove an item from cart — deletes from MongoDB + localStorage.
 * Uses dbId (MongoDB _id) if available, else falls back to service_id match.
 */
export const handleRemoveFromCartGlobal = async (item) => {
  const mobile = getAuthMobile();

  // Update localStorage immediately
  try {
    const existing = JSON.parse(localStorage.getItem("cartItems") || "[]");
    const updated  = existing.filter((i) => String(i.id) !== String(item.id));
    localStorage.setItem("cartItems", JSON.stringify(updated));
    window.dispatchEvent(new Event("cart-updated"));
  } catch (e) { console.error("[Cart] localStorage remove failed:", e); }

  if (!mobile) return;

  // Delete from MongoDB
  try {
    const dbId = item.dbId || "";
    const url  = dbId
      ? `${BASE_URL}/api/user/user-cart/${encodeURIComponent(mobile)}/remove/${encodeURIComponent(dbId)}/`
      : `${BASE_URL}/api/user/user-cart/${encodeURIComponent(mobile)}/remove-by-service/${encodeURIComponent(String(item.id))}/`;
    console.log(`[Cart] DELETE ${url}`);
    const res = await fetch(url, { method: "DELETE", credentials: "include", headers: cartAuthHeaders() });
    console.log(`[Cart] DELETE ← ${res.status}`);
  } catch (err) {
    console.error("[Cart] DELETE failed:", err.message);
  }
};

/**
 * Update qty of an item in cart.
 * qty = 0 → deletes from MongoDB.
 * qty > 0 → PATCH qty in MongoDB.
 */
export const handleQtyChangeGlobal = async (item, newQty) => {
  const mobile = getAuthMobile();

  // Update localStorage immediately
  try {
    const existing = JSON.parse(localStorage.getItem("cartItems") || "[]");
    const updated  = newQty <= 0
      ? existing.filter((i) => String(i.id) !== String(item.id))
      : existing.map((i) => String(i.id) === String(item.id) ? { ...i, qty: newQty } : i);
    localStorage.setItem("cartItems", JSON.stringify(updated));
    window.dispatchEvent(new Event("cart-updated"));
  } catch (e) { console.error("[Cart] localStorage qty update failed:", e); }

  if (!mobile) return;

  const dbId = item.dbId || String(item.id);

  if (newQty <= 0) {
    // Delete from MongoDB
    try {
      const url = item.dbId
        ? `${BASE_URL}/api/user/user-cart/${encodeURIComponent(mobile)}/remove/${encodeURIComponent(item.dbId)}/`
        : `${BASE_URL}/api/user/user-cart/${encodeURIComponent(mobile)}/remove-by-service/${encodeURIComponent(String(item.id))}/`;
      const res = await fetch(url, { method: "DELETE", credentials: "include", headers: cartAuthHeaders() });
      console.log(`[Cart] DELETE ← ${res.status}`);
    } catch (err) { console.error("[Cart] DELETE failed:", err.message); }
  } else {
    // PATCH qty in MongoDB
    try {
      const url = `${BASE_URL}/api/user/user-cart/${encodeURIComponent(mobile)}/update-qty/${encodeURIComponent(dbId)}/`;
      const res = await fetch(url, {
        method: "PATCH", credentials: "include", headers: cartAuthHeaders(),
        body: JSON.stringify({ qty: newQty }),
      });
      console.log(`[Cart] PATCH ← ${res.status}`);
    } catch (err) { console.error("[Cart] PATCH failed:", err.message); }
  }
};

const readCartCount = () => {
  try {
    return JSON.parse(localStorage.getItem("cartItems") || "[]")
      .reduce((s, i) => s + Number(i.qty || 1), 0);
  } catch { return 0; }
};

// ─────────────────────────────────────────────────────────────────────────────
// HEADER COMPONENT
// ─────────────────────────────────────────────────────────────────────────────

// ─────────────────────────────────────────────────────────────────────────────
// CITY PICKER — big "Select the City / Bangalore ▾" box, replaces the old
// search bar's slot in the main header row. Same underlying getUserCity/
// setUserCity + "city-updated" event contract as before, just restyled.
// ─────────────────────────────────────────────────────────────────────────────
const CityPicker = () => {
  const [current, setCurrent] = useState(getUserCity);
  const [open,    setOpen]    = useState(false);
  const ref = useRef(null);

  const cities = Object.entries(config.CITIES || {})
    .filter(([, v]) => v.active)
    .map(([k, v]) => ({ key: k, label: v.label }));

  useEffect(() => {
    const close = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", close);
    return () => document.removeEventListener("mousedown", close);
  }, []);

  const handleSelect = (key) => {
    setUserCity(key);
    setCurrent(key);
    setOpen(false);
    window.dispatchEvent(new Event("city-updated"));
  };

  const label = config.CITIES?.[current]?.label || current;

  return (
    <div ref={ref} style={{ position:"relative" }}>
      <button onClick={() => setOpen(o => !o)} style={S.cityBox}>
        <span style={S.cityIconWrap}><MapPin size={18} color="#2E8B57"/></span>
        <span style={{ textAlign:"left" }}>
          <span style={S.cityLabel}>Select the City</span>
          <span style={S.cityValueRow}>
            <span style={S.cityValue}>{label}</span>
            <ChevronDown size={16} style={{ opacity:0.6 }}/>
          </span>
        </span>
      </button>
      {open && (
        <div style={{ position:"absolute", top:"110%", left:0, background:"#fff", border:"1px solid #e5e7eb", borderRadius:10, boxShadow:"0 6px 20px rgba(0,0,0,0.12)", zIndex:2000, minWidth:200, overflow:"hidden" }}>
          {cities.map(c => (
            <button
              key={c.key}
              onClick={() => handleSelect(c.key)}
              style={{ display:"block", width:"100%", padding:"11px 18px", textAlign:"left", border:"none",
                background: current === c.key ? "#eff6ff" : "#fff",
                color:      current === c.key ? "#1d4ed8"  : "#374151",
                fontWeight: current === c.key ? 700         : 400,
                fontSize:14, cursor:"pointer" }}
            >
              {c.label}
              {current === c.key && <span style={{ float:"right", color:"#1d4ed8" }}>✓</span>}
            </button>
          ))}
        </div>
      )}
    </div>
  );
};

const Header = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [authUser,  setAuthUser]  = useState(() => getSafeLoggedInUser());
  const [cartCount, setCartCount] = useState(readCartCount);

  const refreshAuth  = useCallback(() => setAuthUser(getSafeLoggedInUser()), []);
  const refreshCart  = useCallback(() => setCartCount(readCartCount()), []);

  useEffect(() => {
    refreshAuth();
    refreshCart();
    window.addEventListener("auth-updated", refreshAuth);
    window.addEventListener("cart-updated", refreshCart);
    window.addEventListener("storage",      refreshCart);
    return () => {
      window.removeEventListener("auth-updated", refreshAuth);
      window.removeEventListener("cart-updated", refreshCart);
      window.removeEventListener("storage",      refreshCart);
    };
  }, [refreshAuth, refreshCart]);

  const isLoggedIn = useMemo(
    () => !!(authUser?.token || authUser?.id || authUser?.mobile || authUser?.phone),
    [authUser]
  );

  const displayName = useMemo(() => {
    if (!authUser) return "";
    const name = authUser.name || authUser.full_name || "";
    return name ? name.split(" ")[0] : authUser.mobile || "Account";
  }, [authUser]);

  // Login/Sign Up vs. the avatar link are rendered directly in JSX below
  // (keyed on isLoggedIn) rather than folded into this list, since the
  // avatar now needs its own distinct styling (icon + name + chevron)
  // instead of being just another topLink.
  const topLinks = [
    // Downloads the Partner App instead of navigating to a page — /become-a-professional
    // has no route registered in App.js anyway (was a dead link before this).
    { label: "Become a Professional", icon: <ShieldCheck size={16}/>, path: "https://play.google.com", external: true },
    { label: "Help & Support",        icon: <Headphones  size={16}/>, path: "/contact-us" },
    { label: "Track Order",           icon: <PackageSearch size={16}/>, path: "/track-order" },
  ];

  const quickActions = [
    { label: "Instant Booking",   icon: <Zap size={14}/>,          path: "/services/instant-home-services", color:"#4f46e5", bg:"#eef2ff" },
    { label: "Schedule Booking",  icon: <CalendarDays size={14}/>, path: "/schedule-booking",        color:"#16a34a", bg:"#dcfce7" },
    { label: "Subscription Plan", icon: <Crown size={14}/>,        path: "/subscription-plans",      color:"#d97706", bg:"#fef3c7" },
  ];

  // Same 7 categories used both by the compact pill nav (inner pages) and
  // the big card section (home page) below — description/bg/accent are
  // only consumed by the card version.
  const navItems = [
    { icon: <Home    size={18}/>, label: "Home Services",      path: "/schedule-booking/home-cleaning",
      desc: "Cleaning, Painting, Plumbing & more",     bg:"#e6f7ef", accent:"#2E8B57", image: headerHomeServiceImg },
    { icon: <Monitor size={18}/>, label: "Technical Services", path: "/services/technical-services",
      desc: "AC, TV, Fridge, Laptop & more",           bg:"#e7f0ff", accent:"#2563eb", image: headerTechnicalServiceImg },
    { icon: <Users   size={18}/>, label: "Caretaker Services", path: "/services/caretaker-services",
      desc: "Elder Care, Baby Care, Patient Care & more", bg:"#fdf3df", accent:"#d97706", image: headerCaretakerImg },
    { icon: <Trees   size={18}/>, label: "Outdoor Services",   path: "/services/outdoor-services",
      desc: "Pest Control, Gardening, Disinfection & more", bg:"#e9f7e3", accent:"#16a34a", image: headerOutdoorImg },
    { icon: <Truck   size={18}/>, label: "Transport Services", path: "/services/transport-services",
      desc: "Packers & Movers, Vehicle Rentals & more", bg:"#efeaff", accent:"#6d28d9", image: headerTransportImg },
    { icon: <Star    size={18}/>, label: "Special Services",   path: "/services/special-services",
      desc: "Event Support, Home Deep Cleaning & more", bg:"#fdeaf0", accent:"#db2777", image: headerSpecialServiceImg },
    { icon: <Briefcase size={18}/>, label: "Corporate Services", path: "/services/corporate-services",
      desc: "Office Support, Facilities Management & more", bg:"#e4f6f4", accent:"#0f766e", image: headerCorporateServiceImg },
  ];

  const featureItems = [
    { icon: <BadgeCheck size={20} color="#152B5C"/>, title: "Verified Professionals",   text: "Background checked experts" },
    { icon: <ShieldCheck size={20} color="#152B5C"/>, title: "Transparent Pricing",      text: "No hidden charges" },
    { icon: <Clock3    size={20} color="#152B5C"/>, title: "On-time Service",           text: "Punctual & reliable" },
    { icon: <Headset   size={20} color="#152B5C"/>, title: "24/7 Support",              text: "We are here to help" },
    { icon: <ThumbsUp  size={20} color="#152B5C"/>, title: "Satisfaction Guaranteed",   text: "Quality service, every time" },
  ];

  // There's no standalone cart page — "Cart" and "Summary" both open the
  // same checkout summary screen (SummaryPage reads cartItems from
  // localStorage/navigation state and renders per-item slots + totals).
  const handleSummaryClick = () => {
    const cartItems = (() => { try { return JSON.parse(localStorage.getItem("cartItems") || "[]"); } catch { return []; } })();
    if (isLoggedIn) {
      navigate("/checkout-schedule/summary", { state: { cartItems } });
    } else {
      navigate("/user/user-login", { state: { redirectTo: "/checkout-schedule/summary", cartItems } });
    }
  };
  const handleCartClick = handleSummaryClick;

  // Which of the 4 primary nav pills is "active" — driven by the current
  // route so it's always correct (back button, direct link, etc.), not just
  // whichever was last clicked. Falls back to "book" (Book Service) when the
  // path doesn't match Home/Shopping/Summary, matching the reference design
  // where Book Service is the default-highlighted action.
  const activeNavKey = useMemo(() => {
    const path = location.pathname;
    if (path === "/") return "home";
    if (path.startsWith("/shopping")) return "shop";
    if (path.startsWith("/checkout-schedule/summary") || path.startsWith("/checkout-instant/summary")) return "summary";
    if (path.startsWith("/services/all-services")) return "book";
    return "book";
  }, [location.pathname]);

  const navPillStyle = (key, base) =>
    activeNavKey === key ? { ...base, ...S.navPillActive } : base;

  // Which category in the (now dark-navy) nav row matches the current
  // route, for the teal "active" highlight.
  const activeCategoryPath = useMemo(
    () => navItems.find((n) => location.pathname.startsWith(n.path))?.path || null,
    [location.pathname] // eslint-disable-line react-hooks/exhaustive-deps
  );

  const isHome = location.pathname === "/";

  return (
    <header style={S.header}>
      {/* Utility bar — compact, informational + account/cart shortcuts.
          No city picker here any more (moved to the main header row below). */}
      <div style={S.topBar}>
        <div style={S.topLinksLeft}>
          {topLinks.map((item) => (
            item.external ? (
              <a key={item.label} href={item.path} target="_blank" rel="noopener noreferrer" style={S.topLink}>
                {item.icon}<span>{item.label}</span>
              </a>
            ) : (
              <Link key={item.label} to={item.path} style={S.topLink}>
                {item.icon}<span>{item.label}</span>
              </Link>
            )
          ))}
        </div>

        <div style={S.topLinksRight}>
          {isLoggedIn ? (
            <Link to="/profile-dashboard" style={S.topLink}>
              <LayoutGrid size={16}/><span>{displayName}</span>
            </Link>
          ) : (
            <Link to="/user/user-login" style={S.topLink}><UserCircle2 size={16}/><span>Login</span></Link>
          )}

          <button type="button" onClick={handleCartClick} style={S.cartBtn}>
            <ShoppingCart size={16}/><span>Cart</span>
            <span style={S.cartBadge}>{cartCount}</span>
          </button>

          {quickActions.map((item) => (
            <Link key={item.label} to={item.path} style={{ ...S.quickActionBtn, color: item.color, background: item.bg }}>
              {item.icon}<span>{item.label}</span>
            </Link>
          ))}
        </div>
      </div>

      {/* Main brand header — global (every page): logo, city selector,
          primary navigation actions. Replaces the old home-only hero's
          logo/search/popular-searches block. */}
      <div style={S.mainHeader}>
        <Link to="/" style={S.brandRow}>
          <img src={clickNidoLogo} alt="ClickNido" style={S.logoImg} />
          <span style={S.brandTagline}>We're here to help</span>
        </Link>

        <CityPicker />

        <div style={S.headerActions}>
          <button type="button" onClick={() => navigate("/services/all-services")} style={navPillStyle("book", S.actionBookService)}>
            <CalendarPlus size={16}/>Book Service
          </button>
          <Link to="/shopping" style={navPillStyle("shop", S.actionShopping)}>
            <ShoppingBag size={16}/>Shopping
          </Link>
          <button type="button" onClick={handleSummaryClick} style={navPillStyle("summary", S.actionSummary)}>
            <ShoppingCart size={16}/>{cartCount > 0 ? `Summary (${cartCount})` : "Summary"}
          </button>
          {isLoggedIn ? (
            <Link to="/profile-dashboard" style={S.actionAccount}>
              <UserCircle2 size={17}/><span style={S.avatarName}>{displayName}</span><ChevronDown size={14}/>
            </Link>
          ) : (
            <Link to="/user/user-signup" style={S.actionAccount}>
              <UserCircle2 size={17}/><span>Sign Up</span>
            </Link>
          )}
        </div>
      </div>

      {isHome ? (
        /* Service category cards — home page only. Large, pastel, one per
           category, horizontally scrollable below desktop width instead of
           being squeezed. Same 7 destinations as the compact pill nav. */
        <div style={S.categorySection}>
          <div style={S.categoryRow} className="cn-category-row">
            {navItems.map((item) => (
              <Link key={item.label} to={item.path}
                style={item.image ? S.categoryCardImageWrap : { ...S.categoryCard, background: item.bg }}>
                {item.image ? (
                  // Pre-designed card art (icon/title/desc/arrow already
                  // baked into the image) — same destination link as every
                  // other card, just rendered as a picture instead of markup.
                  <img src={item.image} alt={item.label} style={S.categoryCardImage} />
                ) : (
                  <>
                    <div style={{ ...S.categoryIconWrap, color: item.accent }}>{item.icon}</div>
                    <div style={S.categoryTitle}>{item.label}</div>
                    <div style={S.categoryDesc}>{item.desc}</div>
                    <span style={{ ...S.categoryArrow, color: item.accent, borderColor: item.accent }}>
                      <ArrowRight size={16}/>
                    </span>
                  </>
                )}
              </Link>
            ))}
          </div>
        </div>
      ) : (
        /* Compact pill nav — every other page, unchanged from before. */
        <div style={S.navSection} className="cn-nav-row">
          {navItems.map((item) => (
            <Link
              key={item.label}
              to={item.path}
              className={`cn-nav-item${activeCategoryPath === item.path ? " cn-nav-item-active" : ""}`}
              style={S.navItem}
            >
              {item.icon}<span>{item.label}</span>
            </Link>
          ))}
        </div>
      )}

      {/* Feature bar — compact trust row (informational only, not a link —
          none of these have a real destination page). */}
      <div style={S.featureBar}>
        {featureItems.map((item, idx) => (
          <div
            key={item.title}
            style={{ ...S.featureItem, ...(idx < featureItems.length - 1 ? S.featureItemDivider : {}) }}
          >
            {item.icon}
            <div><h4 style={S.featureTitle}>{item.title}</h4><p style={S.featureText}>{item.text}</p></div>
          </div>
        ))}
      </div>

      <style>{`
        .cn-nav-row, .cn-category-row { -webkit-overflow-scrolling: touch; }
        .cn-nav-row::-webkit-scrollbar, .cn-category-row::-webkit-scrollbar { display: none; }
        .cn-nav-item:hover { background: #2E8B57; color: #ffffff; }
        .cn-nav-item-active { background: #2E8B57; color: #ffffff; }

        /* Cards are fixed-size (categoryCardImage's height), not stretched
           to fill a 1/7 grid column — keeps each card proportionate to the
           rest of the page instead of ballooning to ~230px tall on wide
           screens. space-between still spreads the 7 cards across the
           row's full width (first flush left, last flush right) instead
           of leaving the row's own footprint small. Wraps to extra lines
           on narrow screens instead of needing horizontal scroll. */
        .cn-category-row { display: flex; flex-wrap: wrap; justify-content: space-between; }
      `}</style>
    </header>
  );
};

// Brand palette (spec section 15) — navy for text/nav, sea-green/mint as
// the one accent (logo box, active states); pastel tints only on the
// category cards, one per category.
const NAVY  = "#152B5C";
const MINT  = "#2E8B57";

const S = {
  header: { width:"100%", overflowX:"hidden", backgroundColor:"#ffffff", borderBottom:"1px solid #e5e7eb", fontFamily:"'Segoe UI', Arial, sans-serif" },

  // ── Utility bar ──────────────────────────────────────────────────────────
  topBar: { display:"flex", justifyContent:"space-between", alignItems:"center", padding:"10px 36px", borderBottom:"1px solid #ececec", gap:"16px", overflowX:"auto", scrollbarWidth:"none" },
  topLinksLeft:  { display:"flex", alignItems:"center", gap:"22px", flexShrink:0 },
  topLinksRight: { display:"flex", alignItems:"center", gap:"16px", flexShrink:0 },
  topLink: { display:"flex", alignItems:"center", gap:"7px", fontSize:"13.5px", fontWeight:"600", color:NAVY, textDecoration:"none", whiteSpace:"nowrap", flexShrink:0 },
  cartBtn: { display:"flex", alignItems:"center", gap:"7px", fontSize:"13.5px", fontWeight:"700", color:NAVY, background:"transparent", border:"none", cursor:"pointer", position:"relative", whiteSpace:"nowrap", flexShrink:0 },
  cartBadge: { minWidth:"18px", height:"18px", padding:"0 5px", borderRadius:"999px", background:MINT, color:"#fff", fontSize:"11px", display:"inline-flex", alignItems:"center", justifyContent:"center", fontWeight:"700" },
  quickActionBtn: { display:"flex", alignItems:"center", gap:"6px", fontSize:"12.5px", fontWeight:"700", textDecoration:"none", whiteSpace:"nowrap", flexShrink:0, padding:"7px 13px", borderRadius:"999px" },

  // ── Main brand header — logo, city selector, primary actions ────────────
  mainHeader: { display:"flex", alignItems:"center", gap:"24px", padding:"16px 36px", flexWrap:"wrap" },
  brandRow: { display:"flex", alignItems:"center", gap:"14px", textDecoration:"none", flexShrink:0 },
  logoImg: { height:"52px", width:"auto", objectFit:"contain", display:"block" },
  brandTagline: { fontSize:"14px", fontWeight:"500", color:"#64748b" },

  // City selector — big "Select the City / Bangalore ▾" box.
  cityBox: { display:"flex", alignItems:"center", gap:"10px", background:"#fff", border:"1px solid #e2e8f0", borderRadius:"14px", padding:"9px 18px", cursor:"pointer" },
  cityIconWrap: { display:"flex", alignItems:"center", justifyContent:"center" },
  cityLabel: { display:"block", fontSize:"11.5px", fontWeight:"600", color:"#94a3b8" },
  cityValueRow: { display:"flex", alignItems:"center", gap:"4px" },
  cityValue: { fontSize:"16px", fontWeight:"800", color:NAVY },

  // Primary actions — Book Service / Shopping / Summary / account.
  headerActions: { display:"flex", alignItems:"center", gap:"10px", marginLeft:"auto", flexShrink:0 },
  actionBookService: { display:"flex", alignItems:"center", gap:"8px", fontSize:"14px", fontWeight:"700", color:"#fff", background:NAVY, textDecoration:"none", padding:"11px 20px", borderRadius:"14px", border:"none", cursor:"pointer", whiteSpace:"nowrap" },
  actionShopping:    { display:"flex", alignItems:"center", gap:"8px", fontSize:"14px", fontWeight:"700", color:NAVY, background:"#eaf2ff", textDecoration:"none", padding:"11px 20px", borderRadius:"14px", whiteSpace:"nowrap" },
  actionSummary:     { display:"flex", alignItems:"center", gap:"8px", fontSize:"14px", fontWeight:"700", color:NAVY, background:"#eef0ff", textDecoration:"none", padding:"11px 20px", borderRadius:"14px", border:"none", cursor:"pointer", whiteSpace:"nowrap" },
  actionAccount:     { display:"flex", alignItems:"center", gap:"8px", fontSize:"14px", fontWeight:"700", color:NAVY, background:"#fff", border:`1.5px solid ${NAVY}`, textDecoration:"none", padding:"10px 18px", borderRadius:"14px", whiteSpace:"nowrap" },
  // Applied on top of whichever action's route currently matches.
  navPillActive: { boxShadow:"0 0 0 3px rgba(21,43,92,0.15), 0 6px 16px rgba(0,0,0,0.12)", transform:"translateY(-1px)" },
  avatarName: { maxWidth:"110px", overflow:"hidden", textOverflow:"ellipsis", whiteSpace:"nowrap" },

  // ── Service category cards — home page only ─────────────────────────────
  categorySection: { padding:"6px 36px 22px" },
  // Layout (grid vs. horizontal-scroll flex) is driven by .cn-category-row
  // in the <style> block below, so it can change per breakpoint — inline
  // styles can't do media queries. This just sets the gap, shared by both.
  categoryRow: { gap:"12px", paddingBottom:"4px" },
  categoryCard: { position:"relative", overflow:"hidden", minWidth:0, borderRadius:"18px", padding:"16px 16px", textDecoration:"none", boxShadow:"0 2px 10px rgba(15,23,42,0.05)", display:"flex", flexDirection:"column", gap:"4px" },
  categoryIconWrap: { width:"38px", height:"38px", display:"flex", alignItems:"center", justifyContent:"center", background:"rgba(255,255,255,0.7)", borderRadius:"10px", marginBottom:"4px" },
  categoryTitle: { fontSize:"15.5px", fontWeight:"800", color:NAVY, lineHeight:1.2 },
  categoryDesc:  { fontSize:"12px", fontWeight:"500", color:"#475569", lineHeight:1.35, marginBottom:"10px" },
  categoryArrow: { marginTop:"auto", width:"28px", height:"28px", borderRadius:"50%", background:"#fff", border:"1.5px solid", display:"flex", alignItems:"center", justifyContent:"center" },
  // Pre-designed card art (e.g. Header Home Service.png) — no padding/bg of
  // our own, the image already is the whole card.
  categoryCardImageWrap: { position:"relative", overflow:"hidden", borderRadius:"18px", textDecoration:"none", boxShadow:"0 2px 10px rgba(15,23,42,0.05)", display:"block", lineHeight:0 },
  // Fixed height (not width:100%/height:100% stretched to a 1/7 grid
  // column) — these are square 1254x1254 cards with the full title+button
  // design baked in edge-to-edge, so stretching to fill a wide column made
  // every card ~230px tall. A fixed height keeps the row's footprint
  // proportionate to the rest of the page; width follows automatically
  // since the art is square.
  categoryCardImage: { height:"130px", width:"auto", display:"block", objectFit:"contain" },

  // ── Compact pill nav — every non-home page ──────────────────────────────
  // justify-content:space-between spreads the 7 pills across the bar's
  // full width instead of clustering them left with a large empty gap on
  // the right; falls back to the natural left-packed flow (via gap) once
  // the row is narrow enough to need horizontal scroll.
  navSection: { display:"flex", alignItems:"center", justifyContent:"space-between", padding:"14px 36px", flexWrap:"nowrap", gap:"8px", backgroundColor:NAVY, overflowX:"auto", scrollbarWidth:"none" },
  navItem: { display:"flex", alignItems:"center", gap:"8px", fontSize:"15px", fontWeight:"600", color:"#e5e7eb", textDecoration:"none", padding:"8px 14px", borderRadius:"999px", whiteSpace:"nowrap", flexShrink:0, transition:"background 0.15s, color 0.15s" },

  // ── Trust/benefits row — compact, deliberately lower visual weight than
  // the category cards above it. ───────────────────────────────────────────
  featureBar: { display:"grid", gridTemplateColumns:"repeat(auto-fit,minmax(200px,1fr))", gap:"12px", padding:"14px 36px", backgroundColor:"#f9fafb", borderTop:"1px solid #eef1f5" },
  featureItem: { display:"flex", alignItems:"center", gap:"10px", borderRadius:"12px", padding:"6px 10px", textDecoration:"none", color:"#111827" },
  featureItemDivider: { borderRight:"1px solid #e5e7eb" },
  featureTitle: { margin:0, fontSize:"13.5px", color:"#111827", fontWeight:"700" },
  featureText: { margin:"2px 0 0", fontSize:"12px", color:"#6b7280", lineHeight:"1.4" },
};

export default Header;